import { Box } from "@mui/joy";
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';

export function openSidebar() {
  if (typeof window !== 'undefined') {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.setProperty('--SideNavigation-slideIn', '1');
  }
}

export function closeSidebar() {
  if (typeof window !== 'undefined') {
    document.documentElement.style.removeProperty('--SideNavigation-slideIn');
    document.body.style.removeProperty('overflow');
  }
}

export function toggleSidebar() {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const slideIn = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--SideNavigation-slideIn');
    if (slideIn) {
      closeSidebar();
    } else {
      openSidebar();
    }
  }
}


export const adjustTime = (dateString, hoursToSubtract = 5) => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  date.setHours(date.getHours() - hoursToSubtract);
  
  return date;
};

// Добавляет High-Yield бейджи к Clinical-секциям и окрашивает High-Yield Facts
const addHighYieldBadges = (html) => {
    if (!html) return html;
    const badge = `<span style="display:inline-flex;align-items:center;margin-left:8px;padding:2px 10px;font-size:0.7rem;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;color:#fff;background:#C96B2E;border-radius:20px;vertical-align:middle;line-height:1.4">HIGH YIELD</span>`;
    return html
        // Overview: увеличенный шрифт
        .replace(
            /(<h3)(>)([^<]*\bOverview\b[^<]*)(<\/h3>)/gi,
            `$1 style="font-size:1.6rem"$2$3$4`
        )
        // Clinical-секции: добавляем бейдж (содержимое без < чтобы не выйти за тег)
        .replace(
            /(<h[34][^>]*>)([^<]*\bClinical\b[^<]*)(<\/h[34]>)/gi,
            `$1$2${badge}$3`
        )
        // High-Yield Facts: красим заголовок в оранжевый
        .replace(
            /(<h[34])(>)([^<]*\bHigh[\s-]*Yield\b[^<]*)(<\/h[34]>)/gi,
            `$1 style="color:#C96B2E"$2$3$4`
        );
};

/* ═══════════════════════════════════════════════════════════════════════
   ОБЪЯСНЕНИЯ К ВОПРОСАМ

   В базе объяснения лежат в трёх видах, и раньше все три отдавались прямо
   в dangerouslySetInnerHTML:
     1. готовый HTML (редактор, переводы) — рендерился нормально;
     2. простой текст с абзацами через \n\n — собирался в <p> вручную;
     3. markdown от генератора (# заголовки, **жирный**, списки, таблицы)
        — НЕ рендерился, студент видел сырые # и ** на экране.

   Здесь markdown прогоняется через markdown-it. Побочно чинится ещё одна
   беда: в тексте вида "CD4 < 200" браузер считал "< 200 ... >" тегом и
   съедал кусок объяснения целиком. markdown-it экранирует такой '<'.
   ═══════════════════════════════════════════════════════════════════════ */

// html:true  — готовый HTML внутри markdown проходит как есть
// breaks:true — одиночный \n даёт <br>, как в прежнем ручном рендере
const mdParser = new MarkdownIt({ html: true, breaks: true, linkify: false, typographer: false });

// Заголовки объяснения не должны спорить по размеру с текстом карточки:
// # -> h3, ## -> h4, глубже -> h5. Размер в em, чтобы подстраивался под
// контекст (в «Вопросе дня» текст мельче, чем в сессии теста).
const HEADING_TAG = { 1: 'h3', 2: 'h4' };
const HEADING_SIZE = { 1: '1.15em', 2: '1.05em' };
const headingTag = (token) => HEADING_TAG[Number(token.tag.slice(1))] || 'h5';

mdParser.renderer.rules.heading_open = (tokens, idx) => {
    const level = Number(tokens[idx].tag.slice(1));
    return `<${headingTag(tokens[idx])} style="font-size:${HEADING_SIZE[level] || '1em'};font-weight:700">`;
};
mdParser.renderer.rules.heading_close = (tokens, idx) => `</${headingTag(tokens[idx])}>`;

const EXPL_GREEN = '#0F7B6C';   // правильный вариант — успех
const EXPL_ORANGE = '#D97706';  // дистракторы — внимание

const CORRECT_PATTERNS = [
    /\b([A-E]\s+is\s+correct[:.]?)/gi,
];
const INCORRECT_BASE = [
    /(\(Choice\s+[A-E]\))/g,
    // Генератор пишет разбор дистракторов и так тоже, причём иногда сразу про два
    // варианта: «(Options C and D)».
    /(\(Options?\s+[A-E](?:\s+and\s+[A-E])?\))/g,
    /(\(Вариант\s+[А-Е]\))/g,
    /(\(Ответ\s+[А-Е]\))/g,
    /\b([A-E]\s+is\s+incorrect[:.]?)/gi,
];

// Метка в скобках — всегда разбор дистрактора, отсюда оранжевый.
const DISTRACTOR_LABEL = /^\(\s*(?:Options?|Choice|Вариант|Ответ)\s/i;

// Какой цвет положен метке варианта. null — не метка, красить не надо.
const labelColor = (text) => {
    if (/\bis\s+correct\b/i.test(text)) return EXPL_GREEN;
    if (/\bis\s+incorrect\b/i.test(text)) return EXPL_ORANGE;
    if (DISTRACTOR_LABEL.test(text.trim())) return EXPL_ORANGE;
    return null;
};

// Отличается только последний паттерн — тот, что ловит голую букву «A.».
// В HTML-ветке началом строки служит '>' (как было), в markdown-ветке — перенос
// строки: вариант с '>' откусывал закрывающую скобку у "<br>" и "</strong>",
// тег разваливался и уносил со страницы кусок текста.
const INCORRECT_PATTERNS = [...INCORRECT_BASE, /(?:^|\.\s+|>)\s*([A-E][.)]\s)/g];
const MD_INCORRECT_PATTERNS = [...INCORRECT_BASE, /(?:^|\.\s+|\n)\s*([A-E][.)]\s)/g];

/** color === null — метка остаётся жирной, но чёрной (пресет UWorld). */
const wrapLabel = (label, color) =>
    `<strong style="${color ? `color:${color};` : ''}font-weight:700">${label.trim()}</strong> `;

// Плейсхолдеры из Private Use Area: в медицинском тексте таких символов не
// бывает, и, в отличие от U+0000, markdown-it не подменяет их на U+FFFD.
const PH_OPEN = '\uE000';
const PH_CLOSE = '\uE001';

// Ветка для готового HTML — поведение ровно как до появления markdown.
const decorateHtmlExplanation = (html, plain) => {
    const green = plain ? null : EXPL_GREEN;
    const orange = plain ? null : EXPL_ORANGE;
    let formatted = html;
    CORRECT_PATTERNS.forEach((pattern) => {
        formatted = formatted.replace(pattern, (m, label) => '<br><br>' + wrapLabel(label, green));
    });
    INCORRECT_PATTERNS.forEach((pattern) => {
        formatted = formatted.replace(pattern, (m, label) => '<br><br>' + wrapLabel(label, orange));
    });
    return formatted;
};

// Ветка для текста и markdown.
const renderMarkdownExplanation = (src, plain) => {
    const green = plain ? null : EXPL_GREEN;
    const orange = plain ? null : EXPL_ORANGE;
    // 0. <br> здесь — просто перенос строки (так размечена часть переводов).
    //    Приводим его к \n: иначе паттерн метки варианта съедает '>' у "<br>A. …",
    //    ломает тег и уносит вместе с ним кусок текста. При breaks:true markdown
    //    вернёт этот перенос обратно как <br>.
    let s = src.replace(/<br\s*\/?>/gi, '\n');

    // 1. Прячем **жирный**: без этого регексы меток режут "**Option B is correct.**"
    //    пополам, и на экране остаётся сырое "**Option" (ровно этот баг и был виден).
    const bold = [];
    s = s.replace(/\*\*([^*\n]+?)\*\*/g, (m, inner) => {
        bold.push(inner);
        return `${PH_OPEN}${bold.length - 1}${PH_CLOSE}`;
    });

    // 2. Прячем строки markdown-таблиц: вставка \n\n перед меткой разорвала бы таблицу.
    const tableRows = [];
    s = s.replace(/^[ \t]*\|.*\|[ \t]*$/gm, (m) => {
        tableRows.push(m);
        return `${PH_OPEN}T${tableRows.length - 1}${PH_CLOSE}`;
    });

    // 3. Раскладываем «голые» метки вариантов по абзацам и красим — как раньше.
    CORRECT_PATTERNS.forEach((pattern) => {
        s = s.replace(pattern, (m, label) => '\n\n' + wrapLabel(label, green));
    });
    MD_INCORRECT_PATTERNS.forEach((pattern) => {
        s = s.replace(pattern, (m, label) => '\n\n' + wrapLabel(label, orange));
    });

    // 4. Возвращаем таблицы — обязательно ПЕРЕД жирным: строка таблицы могла
    //    сама содержать **жирный**, и его плейсхолдер уехал внутрь tableRows.
    s = s.replace(new RegExp(`${PH_OPEN}T(\\d+)${PH_CLOSE}`, 'g'), (m, i) => tableRows[Number(i)]);

    // 5. Возвращаем жирный. Метку варианта сразу красим, остальное отдаём markdown.
    s = s.replace(new RegExp(`${PH_OPEN}(\\d+)${PH_CLOSE}`, 'g'), (m, i) => {
        const inner = bold[Number(i)];
        const color = labelColor(inner);
        if (plain) return color ? wrapLabel(inner, null) : `**${inner}**`;
        return color ? wrapLabel(inner, color) : `**${inner}**`;
    });

    return mdParser.render(s);
};

/**
 * Привести объяснение к HTML: markdown -> разметка, метки вариантов -> цвет.
 * Готовый HTML (есть <p>) идёт прежним путём — markdown в нём не встречается,
 * а переверстать 3 800 переведённых объяснений ради этого нечем оправдать.
 */
/**
 * @param {string} html
 * @param {{plain?: boolean}} [opts] plain — не красить метки вариантов
 *   фирменными цветами APEX. Нужно экзаменационному скину: там разбор
 *   набран чёрным, как в эталоне.
 */
export function formatExplanationHtml(html, opts) {
    if (!html || typeof html !== 'string') return html;
    const plain = Boolean(opts && opts.plain);
    return /<p[\s>]/i.test(html)
        ? decorateHtmlExplanation(html, plain)
        : renderMarkdownExplanation(html, plain);
}

// Компонент для безопасного отображения HTML
export const SafeHTML = ({ html, ...props }) => {
    const processedHtml = addHighYieldBadges(html);
    return (
        <Box
            {...props}
            sx={{
                '& img': { maxWidth: '100%', height: 'auto !important' },
                '& table': { width: '100%', borderCollapse: 'collapse' },
                '& th, & td': { border: '1px solid', borderColor: 'neutral.300', p: 1 },
                '& ul, & ol': { pl: 2 },
                ...props.sx
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedHtml) }}
        />
    );
};